import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Repeat, ChevronDown, ChevronUp, PackagePlus, PackageSearch, CheckCircle2, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI } from '../services/api';

const PRIORITY = {
  reference: { label: 'À référencer', cls: 'bg-red-50 text-red-700 border-red-200', icon: PackagePlus, hint: 'Marque demandée mais absente du catalogue' },
  restock: { label: 'À réapprovisionner', cls: 'bg-amber-50 text-amber-800 border-amber-200', icon: PackageSearch, hint: 'Marque au catalogue mais aucune référence en stock' },
  ok: { label: 'En stock', cls: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle2, hint: 'Au moins une référence disponible' },
};

// Admin › Produits : most clicked equivalent brands (table + search) → stocking priorities
const EquivBrandStatsCard = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { adminAPI.equivStats(days).then(setData).catch(() => setData({ brands: [], total_clicks: 0 })); }, [days]);
  if (!data) return null;
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mb-6" data-testid="equiv-stats-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Repeat size={16} className="text-orange-500" /> Marques équivalentes les plus consultées</h3>
          <p className="text-xs text-gray-500" data-testid="equiv-stats-total">{data.total_clicks} clic(s) sur les {days} derniers jours · priorisez le stock des références associées</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={e => setDays(Number(e.target.value))} data-testid="equiv-stats-days" className="text-sm border border-gray-200 rounded-md py-1.5 px-2 bg-white">
            {[7, 30, 90, 365].map(d => <option key={d} value={d}>{d} jours</option>)}
          </select>
          <button type="button" onClick={() => adminAPI.downloadEquivStatsCsv(days).catch(() => toast.error('Export impossible'))} disabled={!data.brands.length} data-testid="equiv-stats-export-csv" title="Exporter en CSV pour vos fournisseurs"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1e3a5f] border border-[#1e3a5f]/30 hover:bg-blue-50 rounded-md px-3 py-1.5 disabled:opacity-50"><FileSpreadsheet size={13} /> Export CSV</button>
        </div>
      </div>
      {!data.brands.length ? (
        <p className="text-sm text-gray-500" data-testid="equiv-stats-empty">Aucun clic enregistré sur cette période. Les clics des visiteurs sur une marque du tableau « Références équivalentes » apparaîtront ici.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600"><tr><th className="text-left py-2 px-3">Marque</th><th className="text-right py-2 px-3">Clics</th><th className="text-left py-2 px-3 w-40">Part</th><th className="text-right py-2 px-3">Au catalogue</th><th className="text-right py-2 px-3">En stock</th><th className="text-left py-2 px-3">Priorité</th><th className="py-2 px-3" /></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {data.brands.map(b => {
                const P = PRIORITY[b.priority] || PRIORITY.ok;
                return (
                  <React.Fragment key={b.brand}>
                    <tr data-testid={`equiv-stats-row-${b.brand}`} className="hover:bg-orange-50/40">
                      <td className="py-2 px-3 font-semibold text-gray-900"><Link to={`/recherche?equiv=${encodeURIComponent(b.brand)}`} target="_blank" rel="noreferrer" className="hover:text-orange-600 hover:underline">{b.brand}</Link></td>
                      <td className="py-2 px-3 text-right font-medium" data-testid={`equiv-stats-clicks-${b.brand}`}>{b.clicks}</td>
                      <td className="py-2 px-3"><div className="h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-orange-500" style={{ width: `${Math.max(b.share, 2)}%` }} /></div><span className="text-[11px] text-gray-500">{b.share} %</span></td>
                      <td className="py-2 px-3 text-right">{b.catalog_products}</td>
                      <td className={`py-2 px-3 text-right font-medium ${b.in_stock_products ? 'text-green-700' : 'text-red-600'}`}>{b.in_stock_products}</td>
                      <td className="py-2 px-3"><span title={P.hint} data-testid={`equiv-stats-priority-${b.brand}`} className={`inline-flex items-center gap-1 text-[11px] font-medium border px-2 py-0.5 rounded-full ${P.cls}`}><P.icon size={11} /> {P.label}</span></td>
                      <td className="py-2 px-3 text-right">{b.refs.length > 0 && <button type="button" onClick={() => setOpen(open === b.brand ? null : b.brand)} data-testid={`equiv-stats-toggle-${b.brand}`} className="text-xs text-blue-600 hover:underline inline-flex items-center gap-0.5">{open === b.brand ? <ChevronUp size={12} /> : <ChevronDown size={12} />} réfs</button>}</td>
                    </tr>
                    {open === b.brand && (
                      <tr className="bg-gray-50/60" data-testid={`equiv-stats-refs-${b.brand}`}>
                        <td colSpan={7} className="py-2 px-3">
                          <ul className="text-xs text-gray-700 space-y-1">
                            {b.refs.map((r, i) => (
                              <li key={i} className="flex flex-wrap items-center gap-2">
                                <span className="font-mono font-semibold">{b.brand} {r.ref}</span> <span className="text-gray-400">· {r.clicks} clic(s)</span>
                                {r.source_product && <span className="text-gray-500">— consulté depuis <Link to={`/produit/${r.source_product.id}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{r.source_product.brand} {r.source_product.ref}</Link></span>}
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EquivBrandStatsCard;
